import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, User, ArrowRight, Phone, ChevronLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { login as loginAction, signup as signupAction, forgotPassword as forgotPasswordAction } from '../../store/slices/authSlice';
import { supabase } from '../../lib/supabase';
import MobileModal from '../shared/MobileModal';
import posthog from 'posthog-js';
import { setPostAuthRedirect, consumePostAuthRedirect, stayHereOr } from '../../utils/postAuthRedirect';

type SignupAccountType = 'creator' | 'individual' | 'buyer';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'signup' | 'forgot-password';
  /** Preselect the account-type radio on the signup branch. Set by the
   *  AuthModalContext when the caller signaled buyer intent (e.g. "Continue
   *  to shop", chat-panel sign-in nudge). Defaults to `'creator'` which
   *  preserves the historical signup behavior. */
  signupAccountType?: SignupAccountType;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, initialMode = 'login', signupAccountType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const isAuthLoading = useSelector((state: RootState) => state.auth.isLoading);
  
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot-password'>(initialMode);
  
  // Update mode when initialMode changes
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
    }
  }, [initialMode, isOpen]);

  // Honor the caller-supplied signup intent on every (re)open. We don't
  // reset to the user's previous selection — the latest open wins, since
  // the context clears intent on close.
  useEffect(() => {
    if (isOpen && signupAccountType) {
      setAccountType(signupAccountType);
    }
  }, [isOpen, signupAccountType]);
  
  // Pre-fill email from query param or location state (e.g. from order email link)
  const urlParams = new URLSearchParams(location.search);
  const prefillEmail = urlParams.get('email') || (location.state as any)?.email || '';

  // Login state
  const [loginEmail, setLoginEmail] = useState(prefillEmail);
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(!!prefillEmail);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Signup state
  const [fullName, setFullName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [heardFrom, setHeardFrom] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Default to 'creator' to preserve historical signup behavior. Buyer is
  // gated below by the `buyer_signup` flag (defaults ON pre-auth).
  const [accountType, setAccountType] = useState<SignupAccountType>('creator');
  const [isSignupLoading, setIsSignupLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{
    fullName?: string;
    email?: string;
    phone?: string;
    password?: string;
    confirmPassword?: string;
    heardFrom?: string;
  }>({});

  // Forgot password state
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [isForgotPasswordLoading, setIsForgotPasswordLoading] = useState(false);
  const [isForgotPasswordSubmitted, setIsForgotPasswordSubmitted] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');

  // Shared state
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // Guards the post-auth navigation to fire EXACTLY ONCE per modal open.
  // The effect below re-runs on unrelated re-renders (its `onClose` dep gets a
  // fresh identity every provider render because the context callback isn't
  // memoized). Without this guard the effect ran twice on the login transition:
  // the first run consumed the stored intent (single-use) and navigated to the
  // right page, then the second run — with the intent already cleared — fell
  // back to the default and bounced the user to it. That's the "lands on the
  // page, then snaps to home" bug. Reset when the modal closes so a later
  // logout→login in the same session still navigates.
  const didPostAuthNavigateRef = React.useRef(false);
  useEffect(() => {
    if (!isOpen) didPostAuthNavigateRef.current = false;
  }, [isOpen]);

  // Redirect if authenticated — fires the moment Redux flips `isAuthenticated`
  // to true, regardless of whether the local handleLoginSubmit await has
  // resolved yet. This makes the modal self-healing: if the login promise is
  // delayed or swallowed (e.g. a slow `/users/me` interceptor retry, a
  // Supabase auth listener side-effect that pumps the event loop, or React
  // unmounting the component before the `finally` block can run
  // setIsLoginLoading(false)), Redux state is the single source of truth and
  // we forcibly clear every local spinner flag here. Without this clear,
  // users saw a stuck "logging in" spinner even though backend logs confirmed
  // login fully succeeded and the tokens were already persisted.
  useEffect(() => {
    if (isAuthenticated && !isAuthLoading && isOpen && !didPostAuthNavigateRef.current) {
      didPostAuthNavigateRef.current = true;
      setIsLoginLoading(false);
      setIsSignupLoading(false);
      setIsGoogleLoading(false);
      // Single, authoritative post-auth navigator. Every successful auth path
      // (email/password, signup, and the Google popup that resolves in-place)
      // flips `isAuthenticated` and lands here, so intent resolution lives in
      // exactly one spot. Stored intent wins; otherwise keep the user on the
      // page they logged in from — never bounce them to home.
      const dest = consumePostAuthRedirect(
        (location.state as any)?.from?.pathname,
        stayHereOr(location.pathname + location.search),
      );
      onClose();
      navigate(dest, { replace: true });
    }
  }, [isAuthenticated, isAuthLoading, isOpen, navigate, onClose, mode, location.state]);

  // Reset form when switching modes
  const switchMode = (newMode: 'login' | 'signup' | 'forgot-password') => {
    setMode(newMode);
    setLoginError('');
    setSignupError('');
    setForgotPasswordError('');
    setFieldErrors({});
    setIsForgotPasswordSubmitted(false);
  };

  const handleGoogleAuth = async () => {
    if (isGoogleLoading) return;
    
    setIsGoogleLoading(true);
    setLoginError('');
    setSignupError('');
    
    try {
      // Persist where to return after the OAuth round-trip. Prefer any
      // router-state intent (set by ProtectedRoute); otherwise fall back to
      // the current page so an in-place Google login lands the user right
      // back where they started instead of on home.
      setPostAuthRedirect(
        (location.state as any)?.from?.pathname || location.pathname + location.search,
      );

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });
      
      if (error) {
        console.error('Google auth error:', error);
        const errorMessage = error.message?.includes('Email link is invalid or has expired')
          ? 'The link has expired. Please try again.'
          : error.message?.includes('User already registered')
          ? 'An account with this email already exists. Signing in will link your accounts.'
          : error.message || 'Failed to authenticate with Google';
        
        if (mode === 'login') {
          setLoginError(errorMessage);
        } else {
          setSignupError(errorMessage);
        }
        toast.error(errorMessage);
        // Only clear loading on failure — on success the browser is about to
        // redirect to Google, so we keep the spinner up through the navigation
        // instead of flashing the button back to its idle state.
        setIsGoogleLoading(false);
      }
    } catch (error: any) {
      console.error('Google auth error:', error);
      const errorMessage = 'An unexpected error occurred';
      if (mode === 'login') {
        setLoginError(errorMessage);
      } else {
        setSignupError(errorMessage);
      }
      toast.error(errorMessage);
      setIsGoogleLoading(false);
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isLoginLoading) return;
    
    setLoginError('');
    setIsLoginLoading(true);
    
    try {
      const result = await dispatch(loginAction({ email: loginEmail, password: loginPassword })).unwrap();
      
      if (result.success) {
        // If backend signals a temp password account, stash flag for soft prompt
        const userData = result.data?.user;
        if (userData?.tempPassword) {
          sessionStorage.setItem('promptPermanentPassword', '1');
          try { posthog.capture('buyer_temp_login_succeeded'); } catch (_) {}
        }

        toast.success('Welcome back to Creators Archive!');
        // Modal close + redirect are handled centrally by the post-auth effect
        // above (which consumes the stored intent). Navigating here as well
        // caused a double-navigation race whose second write always won and
        // clobbered the intended route with `/insights`.
      } else {
        const errorMessage = result.error?.message || 'Login failed. Please check your credentials.';
        
        if (result.error?.details?.emailVerified === false) {
          toast.error('Please verify your email to continue.');
          setTimeout(() => {
            onClose();
            navigate('/auth/confirm', { state: { email: loginEmail }, replace: true });
          }, 1500);
          return;
        }
        
        setLoginError(errorMessage);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      const errorMessage = error.message || 'An unexpected error occurred. Please try again.';
      
      if (error.details?.emailVerified === false) {
        toast.error('Please verify your email to continue.');
        setTimeout(() => {
          onClose();
          navigate('/auth/confirm', { state: { email: loginEmail }, replace: true });
        }, 1500);
        return;
      }
      
      setLoginError(errorMessage);
    } finally {
      setIsLoginLoading(false);
    }
  };

  // Validation functions
  const validateFullName = (name: string): string | undefined => {
    if (!name.trim()) return 'Full name is required';
    if (name.trim().length < 2) return 'Full name must be at least 2 characters';
    if (!/^[a-zA-Z\s]+$/.test(name)) return 'Full name can only contain letters and spaces';
    return undefined;
  };

  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return undefined;
  };

  const validatePhone = (phone: string): string | undefined => {
    if (!phone.trim()) return 'WhatsApp number is required';
    const phoneRegex = /^[+]?[0-9]{10,15}$/;
    if (!phoneRegex.test(phone.replace(/\s/g, ''))) return 'Please enter a valid phone number (10-15 digits)';
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
    return undefined;
  };

  const validateConfirmPassword = (confirmPwd: string, pwd: string): string | undefined => {
    if (!confirmPwd) return 'Please confirm your password';
    if (confirmPwd !== pwd) return 'Passwords do not match';
    return undefined;
  };

  const validateHeardFrom = (value: string): string | undefined => {
    if (!value) return 'Please tell us how you heard about us';
    return undefined;
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isForgotPasswordLoading) return;
    
    setForgotPasswordError('');
    setIsForgotPasswordLoading(true);
    
    try {
      const result = await dispatch(forgotPasswordAction(forgotPasswordEmail)).unwrap();
      
      if (result.success) {
        setIsForgotPasswordSubmitted(true);
        toast.success(result.message || 'Password reset link sent!');
      } else {
        setForgotPasswordError(result.error?.message || 'Failed to send reset link. Please try again.');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setForgotPasswordError('An unexpected error occurred. Please try again.');
    } finally {
      setIsForgotPasswordLoading(false);
    }
  };

  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSignupLoading) return;
    
    setSignupError('');
    setFieldErrors({});
    
    const errors: typeof fieldErrors = {
      fullName: validateFullName(fullName),
      email: validateEmail(signupEmail),
      phone: validatePhone(phone),
      password: validatePassword(signupPassword),
      confirmPassword: validateConfirmPassword(confirmPassword, signupPassword),
      heardFrom: validateHeardFrom(heardFrom),
    };
    
    const hasErrors = Object.values(errors).some(error => error !== undefined);
    if (hasErrors) {
      setFieldErrors(errors);
      setSignupError('Please fix the errors below');
      return;
    }

    if (!termsAccepted) {
      setSignupError('Please accept the Terms of Service and Privacy Policy');
      return;
    }
    
    setIsSignupLoading(true);
    
    try {
      const result = await dispatch(signupAction({
        fullName,
        email: signupEmail,
        phone,
        password: signupPassword,
        heardFrom: heardFrom || undefined,
        termsAccepted,
        accountType,
      })).unwrap();
      
      if (result.success) {
        toast.success('Account created! Please check your email to confirm your account.');
        onClose();
        navigate('/auth/confirm', { 
          state: { email: signupEmail },
          replace: true 
        });
      } else {
        setSignupError(result.error?.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setSignupError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSignupLoading(false);
    }
  };

  // ── Persona (buyer vs designer) drives the adaptive copy and, on signup, the
  //    accountType we register. "I'm shopping" → buyer; "I'm a designer" →
  //    creator (the historical default). The toggle simply reuses accountType
  //    so all downstream signup logic is unchanged. ─────────────────────────
  const persona: 'shopping' | 'designer' = accountType === 'buyer' ? 'shopping' : 'designer';
  const selectPersona = (p: 'shopping' | 'designer') => {
    setAccountType(p === 'shopping' ? 'buyer' : 'creator');
    setLoginError('');
    setSignupError('');
  };

  // Signup-only: the persona toggle picks buyer vs designer, and the copy
  // follows. Login stays neutral (the account already knows its type).
  const SIGNUP_COPY = {
    shopping: { title: "Let's get your outfit started", sub: 'Create an account to order custom outfits and get designer offers.' },
    designer: { title: 'Your work deserves clients', sub: 'Showcase your designs, promote your work, get discovered, and receive custom orders.' },
  } as const;

  const googleCaption = persona === 'shopping'
    ? 'Sign up with your Gmail account. No new password to create.'
    : 'Sign in with the Gmail account already on your phone.';

  const personaToggle = (
    <div className="flex bg-gray-100 rounded-full p-1 mb-5" role="tablist" aria-label="I am a">
      {([['shopping', "I'm shopping"], ['designer', "I'm a designer"]] as const).map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={persona === key}
          onClick={() => selectPersona(key)}
          className={`flex-1 text-sm font-medium py-2 rounded-full transition-all ${
            persona === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const googleButton = (
    <button
      onClick={handleGoogleAuth}
      disabled={isGoogleLoading}
      type="button"
      className="w-full flex items-center gap-3 px-4 py-3.5 bg-gray-900 text-white rounded-full hover:bg-black active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {isGoogleLoading ? (
        <div className="w-5 h-5 mx-auto border-2 border-white/40 border-t-white rounded-full animate-spin" />
      ) : (
        <>
          <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
            <svg className="w-[20px] h-[20px]" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </span>
          <span className="text-[15px] font-semibold text-white">Continue with Google</span>
          <span className="ml-auto text-[11px] font-semibold bg-[#e9b306] text-gray-900 px-2.5 py-1 rounded-full">Fastest</span>
        </>
      )}
    </button>
  );

  const rightSideContent = (
    <div className="p-12 flex items-center justify-center relative overflow-hidden h-full">
      <div className="absolute inset-0">
        <img 
          src={mode === 'forgot-password'
            ? "https://images.unsplash.com/photo-1483985988355-763728e1935b?q=80&w=2070&auto=format&fit=crop"
            : mode === 'login' 
            ? "https://images.unsplash.com/photo-1539109136881-3be0616acf4b?q=80&w=1887&auto=format&fit=crop"
            : "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3"
          }
          alt="Fashion Creator" 
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src = "https://images.unsplash.com/photo-1539109136881-3be0616acf4b?q=80&w=1887&auto=format&fit=crop";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/70"></div>
      </div>
      
      <div className="relative z-10 max-w-lg text-white">
        <h2 className="text-4xl font-bold mb-6">
          {mode === 'forgot-password'
            ? "Secure Your Account"
            : mode === 'login' 
            ? "Africa's Premier Fashion Creators Archive"
            : "Showcase Your African Fashion Excellence"
          }
        </h2>
        <p className="text-lg mb-8 text-gray-100">
          {mode === 'forgot-password'
            ? "We'll help you reset your password and get back to showcasing your amazing fashion creations."
            : mode === 'login'
            ? "Join thousands of verified fashion creators showcasing their talent and connecting with brands across Africa."
            : "Join Africa's premier creators archive and connect with brands looking for authentic fashion professionals and seamstresses."
          }
        </p>
        
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[#e9b306] flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold mb-1">
                {mode === 'forgot-password' ? 'Quick & Easy' : mode === 'login' ? 'Verified Profile' : 'Get Verified'}
              </h3>
              <p className="text-sm text-gray-200">
                {mode === 'forgot-password'
                  ? 'Reset your password in just a few clicks'
                  : mode === 'login' 
                  ? 'Stand out with our trusted verification badge'
                  : 'Professional profile with trusted verification badge'
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[#e9b306] flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold mb-1">
                {mode === 'forgot-password' ? 'Secure Process' : mode === 'login' ? 'Direct Brand Connections' : 'Regular Visibility'}
              </h3>
              <p className="text-sm text-gray-200">
                {mode === 'forgot-password'
                  ? 'Your account security is our top priority'
                  : mode === 'login'
                  ? 'Connect with brands seeking authentic African talent'
                  : 'Featured 3x weekly across our platforms'
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-[#e9b306] flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold mb-1">
                {mode === 'forgot-password' ? '24/7 Support' : mode === 'login' ? 'Regular Promotion' : 'Brand Connections'}
              </h3>
              <p className="text-sm text-gray-200">
                {mode === 'forgot-password'
                  ? 'Need help? Our support team is here for you'
                  : mode === 'login'
                  ? 'Get featured 3x weekly across our platforms'
                  : 'Direct access to brands seeking African talent'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const loginContent = (
    // Logo fills the top empty space (mobile); the form centers in the space
    // below it. Desktop (lg = two-column) keeps normal top flow, no logo.
    <div className="w-full min-h-[calc(100vh-8rem)] flex flex-col lg:min-h-0 lg:block">
      <div className="flex justify-left pt-2 pb-8 lg:hidden">
        <img
          src="/Creatorsarchive1.2@4x.png"
          alt="Creators Archive"
          className="h-9 w-auto animate-logo-heartbeat"
        />
      </div>
      <div className="flex-1 flex flex-col justify-start lg:block">
      {/* No persona toggle here — on login the account already knows whether
          it's a buyer or designer, so we don't ask. */}
      <div className="mb-5">
        <h2 className="text-[22px] font-bold text-gray-900 mb-1">Welcome back</h2>
        <p className="text-[13.5px] text-gray-600 leading-relaxed">Sign in to your account to continue.</p>
      </div>

      {loginError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3 mb-4">
          <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="text-sm">{loginError}</span>
        </div>
      )}

      {googleButton}
      <p className="text-xs text-gray-500 mt-2 mb-4 px-1">Sign in with your Gmail account. No password to remember.</p>

      {!showPasswordField ? (
        <button
          type="button"
          onClick={() => setShowPasswordField(true)}
          className="w-full flex items-center gap-3 px-4 py-3.5 border border-gray-300 rounded-full text-gray-600 hover:border-gray-400 hover:bg-gray-50 active:scale-[0.99] transition-all"
        >
          <Mail size={18} className="text-gray-500" />
          <span className="text-[14px]">Continue with email</span>
          <ArrowRight size={16} className="ml-auto text-gray-300" />
        </button>
      ) : (
        <form onSubmit={handleLoginSubmit} className="space-y-4">
          <div className="relative my-1">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="px-3 bg-white text-gray-400">or use your email</span></div>
          </div>

          <div>
            <label htmlFor="loginEmail" className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                id="authModal-login-email"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                autoFocus={!loginEmail}
                className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#e9b306] focus:ring-2 focus:ring-[#e9b306]/20 outline-none transition-all duration-300"
                placeholder="creator@example.com"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                id="authModal-login-password"
                type={showLoginPassword ? 'text' : 'password'}
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className="w-full pl-12 pr-12 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#e9b306] focus:ring-2 focus:ring-[#e9b306]/20 outline-none transition-all duration-300"
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowLoginPassword(!showLoginPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-all duration-200"
              >
                {showLoginPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-gray-400">
              First time here? Enter the 6-character temporary password from your offers email.
            </p>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center cursor-pointer group">
              <input type="checkbox" className="w-4 h-4 text-[#e9b306] border-gray-300 rounded focus:ring-[#e9b306] transition-all duration-200" />
              <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900 transition-colors duration-200">Remember me</span>
            </label>
            <button
              type="button"
              onClick={() => switchMode('forgot-password')}
              className="text-sm text-[#e9b306] hover:text-[#d4a005] font-medium transition-colors duration-200"
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            disabled={isLoginLoading}
            className="w-full bg-[#e9b306] hover:bg-[#d4a005] text-gray-900 font-semibold py-3.5 rounded-full transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#e9b306]/20 hover:shadow-xl hover:shadow-[#e9b306]/30"
          >
            {isLoginLoading ? (
              <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                Sign In
                <ArrowRight size={20} />
              </>
            )}
          </button>
        </form>
      )}

      <p className="mt-6 text-center text-sm text-gray-600">
        New to Creators Archive?{' '}
        <button onClick={() => switchMode('signup')} className="text-[#e9b306] hover:text-[#d4a005] font-semibold">
          Create account
        </button>
      </p>
      {prefillEmail && (
        <p className="mt-2 text-center text-xs text-gray-400">Ordered before? Your offers email has your sign-in.</p>
      )}
      </div>
    </div>
  );

  const signupContent = (
    // Logo tops the space on mobile; form centers below. When the email form
    // expands past the viewport it simply flows and scrolls normally.
    <div className="w-full min-h-[calc(100vh-8rem)] flex flex-col lg:min-h-0 lg:block">
      <div className="flex justify-center pt-2 pb-12 lg:hidden">
        <img
          src="/Creatorsarchive1.2@4x.png"
          alt="Creators Archive"
          className="h-9 w-auto animate-logo-heartbeat"
        />
      </div>
      <div className="flex-1 flex flex-col justify-start lg:block">
      {personaToggle}

      <div className="mb-5">
        <h2 className="text-[22px] font-bold text-gray-900 mb-1">{SIGNUP_COPY[persona].title}</h2>
        <p className="text-[13.5px] text-gray-600 leading-relaxed">{SIGNUP_COPY[persona].sub}</p>
      </div>

      {signupError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3 mb-4">
          <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="text-sm">{signupError}</span>
        </div>
      )}

      {googleButton}
      <p className="text-xs text-gray-500 mt-2 mb-4 px-1">{googleCaption}</p>

      {!showEmailForm ? (
        <button
          type="button"
          onClick={() => setShowEmailForm(true)}
          className="w-full flex items-center gap-3 px-4 py-3.5 border border-gray-300 rounded-full text-gray-600 hover:border-gray-400 hover:bg-gray-50 active:scale-[0.99] transition-all"
        >
          <Mail size={18} className="text-gray-500" />
          <span className="text-[14px]">Sign up with email</span>
          <ArrowRight size={16} className="ml-auto text-gray-300" />
        </button>
      ) : (
      <form onSubmit={handleSignupSubmit} className="space-y-4">
        <div className="relative my-1">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
          <div className="relative flex justify-center text-xs"><span className="px-3 bg-white text-gray-400">or fill in your details</span></div>
        </div>

        <div>
          <label htmlFor="signup-fullName" className="block text-sm font-medium text-gray-700 mb-2">
            Full Name
          </label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              id="authModal-signup-fullName"
              type="text"
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                if (fieldErrors.fullName) {
                  setFieldErrors(prev => ({ ...prev, fullName: undefined }));
                }
              }}
              autoFocus={!fullName}
              className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border rounded-xl focus:bg-white outline-none transition-all ${
                fieldErrors.fullName
                  ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                  : 'border-gray-200 focus:border-[#e9b306] focus:ring-2 focus:ring-[#e9b306]/20'
              }`}
              placeholder="Your full name"
              required
            />
          </div>
          {fieldErrors.fullName && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.fullName}</p>
          )}
        </div>

        <div>
          <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              id="authModal-signup-email"
              type="email"
              value={signupEmail}
              onChange={(e) => {
                setSignupEmail(e.target.value);
                if (fieldErrors.email) {
                  setFieldErrors(prev => ({ ...prev, email: undefined }));
                }
              }}
              className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border rounded-xl focus:bg-white outline-none transition-all ${
                fieldErrors.email
                  ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                  : 'border-gray-200 focus:border-[#e9b306] focus:ring-2 focus:ring-[#e9b306]/20'
              }`}
              placeholder="creator@example.com"
              required
            />
          </div>
          {fieldErrors.email && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
          )}
        </div>

        <div>
          <label htmlFor="signup-phone" className="block text-sm font-medium text-gray-700 mb-2">
            WhatsApp Number
          </label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              id="authModal-signup-phone"
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                if (fieldErrors.phone) {
                  setFieldErrors(prev => ({ ...prev, phone: undefined }));
                }
              }}
              className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border rounded-xl focus:bg-white outline-none transition-all ${
                fieldErrors.phone
                  ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                  : 'border-gray-200 focus:border-[#e9b306] focus:ring-2 focus:ring-[#e9b306]/20'
              }`}
              placeholder="+234 800 000 0000"
              required
            />
          </div>
          {fieldErrors.phone && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.phone}</p>
          )}
        </div>

        <div>
          <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-2">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              id="authModal-signup-password"
              type={showSignupPassword ? 'text' : 'password'}
              value={signupPassword}
              onChange={(e) => {
                setSignupPassword(e.target.value);
                if (fieldErrors.password) {
                  setFieldErrors(prev => ({ ...prev, password: undefined }));
                }
              }}
              className={`w-full pl-12 pr-12 py-3.5 bg-gray-50 border rounded-xl focus:bg-white outline-none transition-all ${
                fieldErrors.password
                  ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                  : 'border-gray-200 focus:border-[#e9b306] focus:ring-2 focus:ring-[#e9b306]/20'
              }`}
              placeholder="Create a password"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowSignupPassword(!showSignupPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showSignupPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {fieldErrors.password && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>
          )}
        </div>

        <div>
          <label htmlFor="signup-confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
            Confirm Password
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              id="authModal-signup-confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (fieldErrors.confirmPassword) {
                  setFieldErrors(prev => ({ ...prev, confirmPassword: undefined }));
                }
              }}
              className={`w-full pl-12 pr-12 py-3.5 bg-gray-50 border rounded-xl focus:bg-white outline-none transition-all ${
                fieldErrors.confirmPassword
                  ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                  : 'border-gray-200 focus:border-[#e9b306] focus:ring-2 focus:ring-[#e9b306]/20'
              }`}
              placeholder="Confirm your password"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {fieldErrors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.confirmPassword}</p>
          )}
        </div>

        <div>
          <label htmlFor="signup-heardFrom" className="block text-sm font-medium text-gray-700 mb-2">
            How did you hear about us?
          </label>
          <select
            id="authModal-signup-heardFrom"
            value={heardFrom}
            onChange={(e) => {
              setHeardFrom(e.target.value);
              if (fieldErrors.heardFrom) {
                setFieldErrors(prev => ({ ...prev, heardFrom: undefined }));
              }
            }}
            className={`w-full px-4 py-3.5 bg-gray-50 border rounded-xl focus:bg-white outline-none transition-all ${
              fieldErrors.heardFrom
                ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                : 'border-gray-200 focus:border-[#e9b306] focus:ring-2 focus:ring-[#e9b306]/20'
            }`}
            required
          >
            <option value="">Select an option</option>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
            <option value="twitter">Twitter (X)</option>
            <option value="tiktok">TikTok</option>
            <option value="linkedin">LinkedIn</option>
            <option value="friend">Friend or Colleague</option>
            <option value="search_engine">Search Engine</option>
            <option value="fashion_event">Fashion Event</option>
            <option value="blog_article">Blog or Article</option>
            <option value="other">Other</option>
          </select>
          {fieldErrors.heardFrom && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.heardFrom}</p>
          )}
        </div>

        <div className="flex items-start pt-2">
          <input
            id="authModal-signup-terms"
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="w-4 h-4 mt-1 text-[#e9b306] border-gray-300 rounded focus:ring-[#e9b306]"
            required
          />
          <label htmlFor="signup-terms" className="ml-2 text-sm text-gray-600">
            I agree to the{' '}
            <a href="/terms" className="text-[#e9b306] hover:underline font-medium" onClick={onClose}>Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" className="text-[#e9b306] hover:underline font-medium" onClick={onClose}>Privacy Policy</a>
          </label>
        </div>

        <button
          type="submit"
          disabled={isSignupLoading}
          className="w-full bg-[#e9b306] hover:bg-[#d4a005] text-gray-900 font-semibold py-3.5 rounded-full transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#e9b306]/20 hover:shadow-xl hover:shadow-[#e9b306]/30"
        >
          {isSignupLoading ? (
            <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              Create Account
              <ArrowRight size={20} />
            </>
          )}
        </button>
      </form>
      )}

      <p className="mt-6 text-center text-sm text-gray-600">
        Already have an account?{' '}
        <button onClick={() => switchMode('login')} className="text-[#e9b306] hover:text-[#d4a005] font-semibold">
          Sign in
        </button>
      </p>
      </div>
    </div>
  );

  const forgotPasswordContent = (
    <div className="w-full">
      {!isForgotPasswordSubmitted ? (
        <>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Reset your password</h2>
            <p className="text-gray-600">Enter your email to reset your password</p>
          </div>

          {forgotPasswordError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3 mb-6">
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">{forgotPasswordError}</span>
            </div>
          )}

          <form onSubmit={handleForgotPasswordSubmit} className="space-y-5">
            <div>
              <label htmlFor="forgotPasswordEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  id="authModal-forgotPassword-email"
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#e9b306] focus:ring-2 focus:ring-[#e9b306]/20 outline-none transition-all"
                  placeholder="creator@example.com"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isForgotPasswordLoading}
              className="w-full bg-[#e9b306] hover:bg-[#d4a005] text-gray-900 font-semibold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#e9b306]/20"
            >
              {isForgotPasswordLoading ? (
                <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Reset Password
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>

          <button 
            onClick={() => switchMode('login')} 
            className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors w-full"
          >
            <ChevronLeft size={16} />
            Back to login
          </button>
        </>
      ) : (
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Password Reset Sent</h2>
          <p className="text-gray-600 mb-8">
            Password reset instructions have been sent to <strong>{forgotPasswordEmail}</strong>
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Didn't receive it? Check your spam folder or{' '}
            <button 
              onClick={() => setIsForgotPasswordSubmitted(false)}
              className="text-[#e9b306] hover:text-[#d4a005] font-semibold"
            >
              try again
            </button>
          </p>
          <button 
            onClick={() => switchMode('login')} 
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft size={16} />
            Back to login
          </button>
        </div>
      )}
    </div>
  );

  return (
    <MobileModal
      isOpen={isOpen}
      onClose={onClose}
      twoColumnLayout={true}
      rightSideContent={rightSideContent}
      className="!bg-white !backdrop-blur-none"
    >
      {mode === 'login' ? loginContent : mode === 'signup' ? signupContent : forgotPasswordContent}
    </MobileModal>
  );
};

export default AuthModal;
